import React from 'react';
import { MS_TOPICS } from '../../messages/mockData';
import type { MsThread, MsTopicKey } from '../../messages/types';
import { toneStyle } from '../../messages/utils';
import { cn } from '../../lib/cn';
import { Button } from '../Button/Button';
import { Icon } from '../Icon/Icon';
import type { IconKind } from '../Icon/Icon';

const eyebrowClass =
  'font-[var(--font-mono)] text-[9.5px] font-bold uppercase tracking-[0.12em] text-[var(--ink-3)]';

function CardHead({
  icon,
  color,
  label,
  right,
}: {
  icon: IconKind;
  color?: string;
  label: string;
  right?: React.ReactNode;
}) {
  return (
    <div className="flex items-center gap-[7px] border-b border-[var(--cream-2)] px-3.5 py-2.5">
      <Icon kind={icon} size={13} stroke={1.9} color={color ?? 'var(--ink-3)'} />
      <span className={eyebrowClass}>{label}</span>
      {right ? <span className="ml-auto">{right}</span> : null}
    </div>
  );
}

function ConsentLine({ label, granted }: { label: string; granted: boolean }) {
  return (
    <div className="flex items-center gap-[7px] text-xs text-[var(--ink-2)]">
      <Icon kind={granted ? 'shield-check' : 'lock'} size={13} color={granted ? 'var(--success)' : 'var(--terracotta)'} />
      <span>{label} consent:</span>
      <span className="font-semibold" style={{ color: granted ? 'var(--success)' : 'var(--terracotta)' }}>
        {granted ? 'granted' : 'not granted'}
      </span>
    </div>
  );
}

function ConsentCard({ thread }: { thread: MsThread }) {
  const p = thread.patient;
  return (
    <div className="ms-card p-3.5">
      <div className={cn(eyebrowClass, 'mb-2')}>Consent</div>
      <div className="flex flex-col gap-[7px]">
        <ConsentLine label="SMS" granted={p.consent.sms} />
        <ConsentLine label="Email" granted={p.consent.email} />
      </div>
    </div>
  );
}

function TopicRow({
  active,
  onClick,
  dot,
  label,
  count,
}: {
  active: boolean;
  onClick: () => void;
  dot: string;
  label: string;
  count: number;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="flex w-full cursor-pointer items-center gap-2.5 rounded-[var(--r-sm)] border-0 px-[11px] py-2.5 text-left font-[var(--font-body)] transition-[background] duration-[140ms]"
      style={{ background: active ? 'var(--ai-tint)' : 'transparent' }}
      onMouseEnter={(e) => {
        if (!active) e.currentTarget.style.background = 'var(--cream-0)';
      }}
      onMouseLeave={(e) => {
        if (!active) e.currentTarget.style.background = 'transparent';
      }}
    >
      <span className="h-2 w-2 shrink-0 rounded-full" style={{ background: dot }} />
      <span
        className="flex-1 text-[13.5px]"
        style={{ fontWeight: active ? 600 : 500, color: active ? 'var(--ai-3)' : 'var(--ink-1)' }}
      >
        {label}
      </span>
      <span
        className="rounded-full border border-[var(--cream-2)] px-2 py-px font-[var(--font-mono)] text-[11.5px] font-bold"
        style={{
          color: active ? 'var(--ai-3)' : 'var(--ink-3)',
          background: active ? 'var(--cream-1)' : 'var(--cream-0)',
        }}
      >
        {count}
      </span>
    </button>
  );
}

function ConversationTab({
  thread,
  topic,
  setTopic,
  onGenerateSummary,
  summaryGenerating = false,
}: {
  thread: MsThread;
  topic: string;
  setTopic: (t: string) => void;
  onGenerateSummary?: () => void;
  summaryGenerating?: boolean;
}) {
  const atts = thread.messages.flatMap((m) => m.attachments ?? []);

  return (
    <div className="flex flex-col gap-3 px-4 py-3.5 pb-6">
      {thread.triage ? (
        <div
          className="ms-card p-3.5"
          style={{
            background: 'var(--terracotta-tint)',
            borderColor: 'color-mix(in srgb, var(--terracotta) 26%, transparent)',
          }}
        >
          <div className="flex items-center gap-2">
            <span className="inline-flex h-[30px] w-[30px] shrink-0 items-center justify-center rounded-lg bg-[var(--cream-1)] text-[var(--terracotta)]">
              <Icon kind="alert" size={15} stroke={2} />
            </span>
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-1.5">
                <span className="whitespace-nowrap rounded bg-[var(--cream-1)] px-[7px] py-px font-[var(--font-mono)] text-[10.5px] font-bold text-[var(--terracotta)]">
                  {thread.triage.id}
                </span>
                <span className={cn(eyebrowClass, 'text-[9px] text-[var(--terracotta)]')}>In Triage Inbox</span>
              </div>
              <div className="mt-1 text-[12.5px] font-semibold text-[var(--ink-0)]">{thread.triage.title}</div>
            </div>
          </div>
          <button
            type="button"
            className="ms-chip mt-2.5 w-full justify-center py-1.5 text-[var(--terracotta)]"
            style={{
              borderColor: 'color-mix(in srgb, var(--terracotta) 30%, transparent)',
              background: 'var(--cream-1)',
            }}
          >
            Open in Triage <Icon kind="external" size={11} />
          </button>
        </div>
      ) : null}

      <div className="ms-card overflow-hidden">
        <CardHead
          icon="sparkle"
          color="var(--ai-3)"
          label="Live summary"
          right={<span className="font-[var(--font-mono)] text-[10px] text-[var(--ink-4)]">{thread.when}</span>}
        />
        {thread.summary ? (
          <div className="px-3.5 py-3 text-[13px] leading-[1.6] text-[var(--ink-1)]">{thread.summary}</div>
        ) : (
          <div className="flex flex-col items-start gap-2 px-3.5 py-3">
            <p className="text-[12.5px] leading-[1.5] text-[var(--ink-3)]">
              No summary yet. Generating one also files it as this patient&apos;s chart note.
            </p>
            {thread.episodeId != null && onGenerateSummary ? (
              <Button
                variant="secondary"
                size="sm"
                iconLeft="sparkle"
                disabled={summaryGenerating}
                onClick={onGenerateSummary}
              >
                {summaryGenerating ? 'Generating…' : 'Generate summary'}
              </Button>
            ) : null}
          </div>
        )}
      </div>

      <TopicsSection thread={thread} topic={topic} setTopic={setTopic} />

      {thread.actions.length > 0 ? (
        <div>
          <div className="font-[var(--font-display)] text-[14.5px] font-semibold text-[var(--ink-0)]">Just done</div>
          <div className="mb-2.5 mt-0.5 text-[11.5px] text-[var(--ink-3)]">Auto-executed by Elva · most recent first</div>
          <div className="flex flex-col gap-2.5">
            {thread.actions.map((a, i) => {
              const tn = toneStyle(a.tone);
              return (
                <div key={i} className="ms-card p-3">
                  <div className="mb-2 flex items-start gap-2.5">
                    <span
                      className="inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-lg"
                      style={{ background: tn.bg, color: tn.fg }}
                    >
                      <Icon kind={a.icon} size={14} stroke={1.9} />
                    </span>
                    <div className="min-w-0 flex-1">
                      <div className="text-[13.5px] font-semibold text-[var(--ink-0)]">{a.title}</div>
                      <div className="mt-0.5 text-xs leading-[1.45] text-[var(--ink-3)]">{a.detail}</div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 border-t border-[var(--cream-2)] pt-2">
                    <span
                      className="ms-risk"
                      style={{
                        background:
                          a.risk === 'high'
                            ? 'var(--terracotta-tint)'
                            : a.risk === 'med'
                              ? 'var(--gold-tint)'
                              : 'var(--success-tint)',
                        color:
                          a.risk === 'high'
                            ? 'var(--terracotta)'
                            : a.risk === 'med'
                              ? 'var(--gold)'
                              : 'var(--success)',
                      }}
                    >
                      {a.risk === 'high' ? 'High' : a.risk === 'med' ? 'Medium' : 'Low risk'}
                    </span>
                    <span className="font-[var(--font-mono)] text-[10.5px] text-[var(--ink-4)]">{a.time}</span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      ) : (
        <div className="px-5 py-6 text-center text-[12.5px] text-[var(--ink-4)]">
          <Icon kind="check-circ" size={22} stroke={1.6} color="var(--ink-4)" className="mx-auto mb-1.5" />
          Nothing automated on this thread yet.
        </div>
      )}

      <ConsentCard thread={thread} />

      {atts.length > 0 ? (
        <div className="ms-card overflow-hidden">
          <CardHead icon="image" color="var(--ai-3)" label={`Attachments · ${atts.length}`} />
          <div className="flex flex-col gap-2 p-3.5">
            {atts.map((a, i) => (
              <div
                key={i}
                className="flex items-center gap-2.5 rounded-[var(--r-sm)] border border-[var(--cream-2)] bg-[var(--cream-0)] px-2.5 py-2"
              >
                <span className="ms-thumb h-10 w-10">
                  <Icon kind="image" size={18} color="var(--ai-3)" />
                </span>
                <div className="min-w-0 flex-1">
                  <div className="truncate text-[12.5px] font-semibold text-[var(--ink-0)]">{a.name}</div>
                  <div className="mt-px font-[var(--font-mono)] text-[10.5px] text-[var(--ink-4)]">{a.size}</div>
                </div>
                <button type="button" className="ms-chip px-2 py-[5px] text-[11px]" title="Download">
                  <Icon kind="download" size={12} />
                </button>
              </div>
            ))}
          </div>
        </div>
      ) : null}
    </div>
  );
}

function TopicsSection({
  thread,
  topic,
  setTopic,
}: {
  thread: MsThread;
  topic: string;
  setTopic: (t: string) => void;
}) {
  return (
    <div className="flex flex-col gap-2">
      <div>
        <div className={eyebrowClass}>Topics in thread</div>
        <div className="mt-1 text-[11px] text-[var(--ink-3)]">
          Elva detects every topic across the thread. Tap one to filter.
        </div>
      </div>
      <div className="ms-card flex flex-col gap-1 p-2">
        <TopicRow
          active={topic === 'all'}
          onClick={() => {
            setTopic('all');
          }}
          dot="var(--ai-3)"
          label="All messages"
          count={thread.messages.length}
        />
        {[...thread.topics].reverse().map((k) => {
          const tp = MS_TOPICS[k as MsTopicKey];
          if (!tp) return null;
          const n = thread.messages.filter((m) => m.topic === k).length;
          return (
            <TopicRow
              key={k}
              active={topic === k}
              onClick={() => {
                setTopic(topic === k ? 'all' : k);
              }}
              dot={tp.color}
              label={tp.label}
              count={n}
            />
          );
        })}
      </div>
      {topic !== 'all' ? (
        <button
          type="button"
          className="ms-chip justify-center py-[7px]"
          onClick={() => {
            setTopic('all');
          }}
        >
          <Icon kind="x" size={11} /> Clear filter — show all
        </button>
      ) : null}
    </div>
  );
}

export interface MessagesRightPanelProps {
  thread: MsThread;
  topic: string;
  setTopic: (t: string) => void;
  /** D23 — request an episode summary (shown when the open episode has none, e.g. autopilot off). */
  onGenerateSummary?: () => void;
  summaryGenerating?: boolean;
}

export function MessagesRightPanel({
  thread,
  topic,
  setTopic,
  onGenerateSummary,
  summaryGenerating = false,
}: MessagesRightPanelProps) {
  const n = thread.actions.length;

  return (
    <div
      style={{
        width: 384,
        flex: 'none',
        display: 'flex',
        flexDirection: 'column',
        borderLeft: '1px solid var(--cream-2)',
        background: 'var(--cream-0)',
        minHeight: 0,
      }}
    >
      <div className="flex shrink-0 border-b border-[var(--cream-2)] px-1.5">
        <button type="button" className="ms-tab active">
          Conversation
          {n > 0 ? (
            <span className="rounded-[5px] bg-[var(--ai-tint)] px-1.5 py-px font-[var(--font-mono)] text-[10px] font-bold text-[var(--ai-3)]">
              {n}
            </span>
          ) : null}
        </button>
      </div>
      <div className="cal-scroll min-h-0 flex-1 overflow-y-auto">
        <ConversationTab
          thread={thread}
          topic={topic}
          setTopic={setTopic}
          summaryGenerating={summaryGenerating}
          {...(onGenerateSummary ? { onGenerateSummary } : {})}
        />
      </div>
    </div>
  );
}
